/**
 * useAutoResizeTextarea Hook
 *
 * Automatically resizes a textarea based on its content.
 * Uses scrollHeight to calculate required height.
 *
 * Inspired by: vercel-ai-chatbot/components/multimodal-input.tsx (lines 86-96)
 * Simplified for broader browser compatibility (no field-sizing-content CSS)
 *
 * @param textareaRef - Ref to the textarea element
 * @param value - Current textarea value (triggers resize)
 * @param minHeight - Minimum height in pixels (default: 44)
 * @param maxHeight - Maximum height in pixels (default: 200)
 */

import { useEffect } from 'react';

interface UseAutoResizeTextareaOptions {
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  value: string;
  minHeight?: number;
  maxHeight?: number;
}

export function useAutoResizeTextarea({
  textareaRef,
  value,
  minHeight = 44,
  maxHeight = 200,
}: UseAutoResizeTextareaOptions) {
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    if (!value) {
      textarea.style.height = `${minHeight}px`;
      textarea.style.overflowY = 'hidden';
      return;
    }

    // Reset height to min to get accurate scrollHeight
    textarea.style.height = `${minHeight}px`;

    // Calculate new height based on content
    const scrollHeight = textarea.scrollHeight;

    // Set height, capped at maxHeight
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;

    // Show scrollbar if content exceeds maxHeight
    if (scrollHeight > maxHeight) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  }, [value, textareaRef, minHeight, maxHeight]);
}
